import { useCallback, useState } from 'react'
import useSWR from 'swr'

import { useAuthToken } from '@/hooks/useAuthToken'
import { ApiError, createFetcher, createUpdater } from '@/services/api'
import { coachProfileService } from '@/services/coachProfileService'
import type { CoachProfileRead, CoachSelfProfilePayload } from '@/types/coachProfile'

const SWR_OPTS = { revalidateOnFocus: false, revalidateOnReconnect: false } as const

/**
 * The signed-in coach's own profile, and the call that writes it.
 *
 * Shaped like `useCoachAvailability`: the URL is the key, and a save seeds the
 * cache with the response rather than revalidating, because the response *is* what
 * the write produced.
 *
 * The payload is partial by contract — a field left out is untouched and a field
 * sent as null is cleared — so the editor sends the whole form and a caller
 * clearing one thing sends one field.
 */
export function useMyCoachProfile(enabled = true) {
  const { getToken, isSignedIn } = useAuthToken()
  const key = isSignedIn && enabled ? coachProfileService.getMyProfileUrl() : null

  const { data, error, isLoading, mutate } = useSWR<CoachProfileRead>(key, createFetcher(getToken), SWR_OPTS)

  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)

  const save = useCallback(
    async (payload: CoachSelfProfilePayload): Promise<CoachProfileRead | null> => {
      if (!key) return null
      setIsSaving(true)
      setSaveError(null)
      try {
        const saved = await createUpdater(getToken)<CoachProfileRead>(key, payload)
        await mutate(saved, { revalidate: false })
        return saved
      } catch (caught) {
        // Kept rather than thrown on: a failed save wrote nothing, and the
        // editor's job is to say so and keep what the coach typed.
        setSaveError(caught instanceof ApiError ? caught : new ApiError(String(caught), 0))
        return null
      } finally {
        setIsSaving(false)
      }
    },
    [getToken, key, mutate]
  )

  return {
    profile: data,
    isLoading,
    loadError: error as ApiError | undefined,
    save,
    isSaving,
    saveError,
    clearSaveError: useCallback(() => setSaveError(null), []),
  }
}
